import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { RefreshCcw, Play, CheckCircle, AlertCircle, BarChart2, ChevronDown, ChevronUp, ZoomIn, ZoomOut, RotateCcw, X, ChevronLeft, ChevronRight, Settings, Trash2 } from 'lucide-react';

const ImageModal = ({ isOpen, onClose, imageUrl, imageUrls, lotNum, items }) => {
  const colors = [
    'border-red-500 bg-red-500',
    'border-blue-500 bg-blue-500',
    'border-green-500 bg-green-500',
    'border-yellow-500 bg-yellow-500',
    'border-purple-500 bg-purple-500',
    'border-orange-500 bg-orange-500',
    'border-pink-500 bg-pink-500',
    'border-cyan-500 bg-cyan-500',
    'border-indigo-500 bg-indigo-500',
    'border-teal-500 bg-teal-500',
  ];
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (imageUrls && imageUrl) {
      // Find index by comparing path parts of URLs to be robust against SAS token differences
      const getPath = (url) => url ? url.split('?')[0] : '';
      const targetPath = getPath(imageUrl);
      const idx = imageUrls.findIndex(url => getPath(url) === targetPath);
      if (idx !== -1) setCurrentIndex(idx);
    }
  }, [imageUrl, imageUrls]);

  if (!isOpen) return null;

  const currentUrl = imageUrls && imageUrls.length > 0 ? imageUrls[currentIndex] : imageUrl;

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.2, 5));
  const handleZoomOut = () => {
    setZoom(prev => {
      const newZoom = Math.max(prev - 0.2, 0.5);
      if (newZoom === 1) setOffset({ x: 0, y: 0 });
      return newZoom;
    });
  };
  const resetZoom = () => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const nextImage = (e) => {
    e.stopPropagation();
    if (imageUrls && currentIndex < imageUrls.length - 1) {
      setCurrentIndex(currentIndex + 1);
      resetZoom();
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (imageUrls && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      resetZoom();
    }
  };

  let title = `Lot #${lotNum}`;
  const itemsList = Array.isArray(items) ? items : (items ? [items] : []);
  if (itemsList.length === 1 && itemsList[0].bounding_boxes && Array.isArray(itemsList[0].bounding_boxes)) {
    const bbEntry = itemsList[0].bounding_boxes[currentIndex];
    if (bbEntry && typeof bbEntry === 'object' && bbEntry.label) {
      title = `${bbEntry.label} (Lot #${lotNum})`;
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4" onClick={onClose}>
      <div className="relative max-w-5xl w-full bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-4 border-b bg-white z-10">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold text-slate-800">{title} {imageUrls && imageUrls.length > 1 ? `(${currentIndex + 1}/${imageUrls.length})` : ''}</h3>
            <div className="flex items-center bg-slate-100 rounded-lg p-1 gap-1">
              <button 
                onClick={handleZoomOut}
                className="p-1.5 hover:bg-white hover:shadow-sm rounded transition-all text-slate-600"
                title="Zoom Out"
              >
                <ZoomOut size={18} />
              </button>
              <span className="text-xs font-mono w-12 text-center text-slate-500">{Math.round(zoom * 100)}%</span>
              <button 
                onClick={handleZoomIn}
                className="p-1.5 hover:bg-white hover:shadow-sm rounded transition-all text-slate-600"
                title="Zoom In"
              >
                <ZoomIn size={18} />
              </button>
              <button 
                onClick={resetZoom}
                className="p-1.5 hover:bg-white hover:shadow-sm rounded transition-all text-slate-600 ml-1 border-l pl-2"
                title="Reset Zoom"
              >
                <RotateCcw size={18} />
              </button>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-full transition-colors">
            <X size={24} className="text-slate-500" />
          </button>
        </div>
        
        <div 
          className={`relative flex-1 overflow-hidden bg-slate-900 flex items-center justify-center ${zoom > 1 ? 'cursor-grab' : 'cursor-default'} ${isDragging ? 'cursor-grabbing' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div 
            className="relative transition-transform duration-200 ease-out origin-center"
            style={{ 
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            }}
          >
            <img 
              src={currentUrl} 
              alt={`Lot ${lotNum}`} 
              className="max-w-full max-h-[70vh] w-auto h-auto block select-none pointer-events-none" 
            />
            {items && items.map((item, idx) => {
              const colorClass = colors[idx % colors.length];
              const [borderColor, bgColor] = colorClass.split(' ');
              
              // Use multiple bounding boxes if available, otherwise fallback to single one for first image
              let currentBox = null;
              let currentLabel = item.item || item.item_name;

              if (item.bounding_boxes && Array.isArray(item.bounding_boxes)) {
                const bbEntry = item.bounding_boxes[currentIndex];
                if (bbEntry && typeof bbEntry === 'object' && bbEntry.box) {
                  currentBox = bbEntry.box;
                  currentLabel = bbEntry.label || currentLabel;
                } else {
                  currentBox = bbEntry;
                }
              } else if (currentIndex === 0) {
                currentBox = item.bounding_box;
              }

              // Ignore [0,0,0,0] boxes which might be returned by some models despite instructions
              if (Array.isArray(currentBox) && currentBox.every(val => val === 0)) {
                currentBox = null;
              }

              return currentBox && (
                <div 
                  key={`bb-${idx}`}
                  className={`absolute border-2 ${borderColor} ${bgColor} bg-opacity-10 pointer-events-none`}
                  style={{
                    top: `${currentBox[0] / 10}%`,
                    left: `${currentBox[1] / 10}%`,
                    height: `${(currentBox[2] - currentBox[0]) / 10}%`,
                    width: `${(currentBox[3] - currentBox[1]) / 10}%`,
                  }}
                >
                  <span className={`absolute -top-6 left-0 ${bgColor} text-white text-[10px] px-1 rounded whitespace-nowrap shadow-sm`}>
                    {currentLabel}
                  </span>
                </div>
              );
            })}
          </div>

          {imageUrls && currentIndex > 0 && (
            <button 
              onClick={prevImage}
              className="absolute left-4 z-50 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full transition-all shadow-lg"
            >
              <ChevronLeft size={32} />
            </button>
          )}

          {imageUrls && currentIndex < imageUrls.length - 1 && (
            <button 
              onClick={nextImage}
              className="absolute right-4 z-50 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full transition-all shadow-lg"
            >
              <ChevronRight size={32} />
            </button>
          )}
        </div>

        {imageUrls && imageUrls.length > 1 && (
          <div className="p-2 bg-slate-800 flex justify-center gap-2 overflow-x-auto border-t border-slate-700">
            {imageUrls.map((url, idx) => (
              <img 
                key={idx}
                src={url}
                onClick={() => { setCurrentIndex(idx); resetZoom(); }}
                className={`w-12 h-12 object-cover rounded cursor-pointer transition-all ${currentIndex === idx ? 'ring-2 ring-blue-500 scale-105' : 'opacity-60 hover:opacity-100'}`}
                alt={`Thumbnail ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const TestRigPage = () => {
  const [testResults, setTestResults] = useState([]);
  const [isRunning, setIsRunning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRun, setExpandedRun] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedLotNum, setSelectedLotNum] = useState(null);
  const [selectedItems, setSelectedItems] = useState(null);
  const [modelName, setModelName] = useState('gpt-4o');
  const [isChangingModel, setIsChangingModel] = useState(false);
  const [resizeMode, setResizeMode] = useState('1024');
  const [isChangingResizeMode, setIsChangingResizeMode] = useState(false);
  const [compressionMode, setCompressionMode] = useState('medium');
  const [isChangingCompressionMode, setIsChangingCompressionMode] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusRes, resultsRes, modelRes, resizeRes, compressionRes] = await Promise.all([
        axios.get('/test_status'),
        axios.get('/get_test_results'),
        axios.get('/get_model'),
        axios.get('/get_resize_mode'),
        axios.get('/get_compression_mode')
      ]);
      setIsRunning(statusRes.data.is_running);
      setTestResults(resultsRes.data);
      setModelName(modelRes.data.model);
      setResizeMode(resizeRes.data.resize_mode || '1024');
      setCompressionMode(compressionRes.data.compression_mode || 'medium');
    } catch (error) {
      console.error("Error fetching test rig data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleModelChange = async (newModel) => {
    try {
      setIsChangingModel(true);
      await axios.post('/set_model', { model: newModel });
      setModelName(newModel);
    } catch (error) {
      console.error('Error setting model name:', error);
      alert('Failed to update model.');
    } finally {
      setIsChangingModel(false);
    }
  };

  const handleResizeModeChange = async (newResizeMode) => {
    try {
      setIsChangingResizeMode(true);
      const response = await axios.post('/set_resize_mode', { resize_mode: newResizeMode });
      setResizeMode(response.data.resize_mode || newResizeMode);
    } catch (error) {
      console.error('Error setting resize mode:', error);
      alert('Failed to update resize mode.');
    } finally {
      setIsChangingResizeMode(false);
    }
  };

  const handleCompressionModeChange = async (newCompressionMode) => {
    try {
      setIsChangingCompressionMode(true);
      const response = await axios.post('/set_compression_mode', { compression_mode: newCompressionMode });
      setCompressionMode(response.data.compression_mode || newCompressionMode);
    } catch (error) {
      console.error('Error setting compression mode:', error);
      alert('Failed to update compression mode.');
    } finally {
      setIsChangingCompressionMode(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
        if (isRunning) fetchData();
    }, 5000);
    return () => clearInterval(interval);
  }, [isRunning]);

  const runTestRig = async () => {
    if (isRunning) return;
    try {
      const response = await axios.post('/run_test_rig', {
        resize_mode: resizeMode,
        compression_mode: compressionMode
      });
      if (response.data.status === 'success') {
        setIsRunning(true);
        if (response.data.resize_mode) {
          setResizeMode(response.data.resize_mode);
        }
        if (response.data.compression_mode) {
          setCompressionMode(response.data.compression_mode);
        }
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        alert(error.response.data.message);
        setIsRunning(true); // Sync state if server says it's already running
      } else {
        alert("Error starting test rig: " + (error.response?.data?.message || error.message));
      }
    }
  };

  const flushResults = async () => {
    if (!window.confirm("Are you sure you want to clear all test results? This cannot be undone.")) return;
    if (isRunning) {
        alert("Cannot flush results while test rig is running.");
        return;
    }
    
    try {
      const response = await axios.post('/flush_test_results');
      if (response.data.status === 'success') {
        setTestResults([]);
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error("Error flushing results:", error);
      alert("Failed to flush results.");
    }
  };

  const stats = {
    avgSpeed: testResults.length > 0 
      ? (testResults.reduce((acc, curr) => acc + (curr.avg_speed_ms || curr.avg_speed || 0), 0) / testResults.length).toFixed(0) 
      : '-',
    avgAccuracy: testResults.length > 0 
      ? (testResults.reduce((acc, curr) => acc + (curr.avg_accuracy || 0), 0) / testResults.length * 100).toFixed(1) + '%' 
      : '-',
    totalCost: testResults.length > 0 
      ? testResults.reduce((acc, curr) => acc + (curr.total_cost || 0), 0).toFixed(4) 
      : '-',
    totalRuns: testResults.length
  };

  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-6 bg-white rounded-lg shadow-md">
      <div className="flex flex-col gap-3 mb-6 sm:mb-8">
        {/* Row 1: Title + Dataset badge */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Test Rig</h1>
          <div className="flex items-center px-3 sm:px-4 py-2 bg-blue-50 text-blue-700 rounded border border-blue-100">
            <BarChart2 size={18} className="mr-2 flex-shrink-0" />
            <span className="text-xs sm:text-sm font-medium">Dataset: American Liquidations (58 items)</span>
          </div>
        </div>
        {/* Row 2: Model selector + Action buttons */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="flex items-center bg-slate-50 p-2 rounded-lg border border-slate-200">
              <Settings size={18} className="text-slate-500 mr-2" />
              <span className="text-sm font-medium text-slate-700 mr-2">Model:</span>
              {isChangingModel ? (
                <span className="text-xs text-slate-400">Updating...</span>
              ) : (
                <select 
                  value={modelName}
                  onChange={(e) => handleModelChange(e.target.value)}
                  className="bg-white border border-slate-300 rounded px-2 py-1 outline-none focus:border-blue-500 text-xs font-medium cursor-pointer"
                >
                  <optgroup label="GPT-5 Series (Latest)">
                    <option value="gpt-5.4">gpt-5.4</option>
                    <option value="gpt-5.4-mini">gpt-5.4-mini</option>
                    <option value="gpt-5.2">gpt-5.2</option>
                    <option value="gpt-5.1">gpt-5.1</option>
                    <option value="gpt-5">gpt-5</option>
                  </optgroup>
                  <optgroup label="GPT-4 Series">
                    <option value="gpt-4o">gpt-4o</option>
                    <option value="gpt-4o-mini">gpt-4o-mini</option>
                    <option value="gpt-4.1">gpt-4.1</option>
                    <option value="gpt-4.1-mini">gpt-4.1-mini</option>
                  </optgroup>
                  <optgroup label="Reasoning Models">
                    <option value="o1">o1</option>
                    <option value="o3-mini">o3-mini</option>
                    <option value="o3">o3</option>
                    <option value="o4-mini">o4-mini</option>
                  </optgroup>
                </select>
              )}
            </div>
            <div className="flex items-center bg-slate-50 p-2 rounded-lg border border-slate-200">
              <span className="text-sm font-medium text-slate-700 mr-2">Resize:</span>
              {isChangingResizeMode ? (
                <span className="text-xs text-slate-400">Updating...</span>
              ) : (
                <select
                  value={resizeMode}
                  onChange={(e) => handleResizeModeChange(e.target.value)}
                  className="bg-white border border-slate-300 rounded px-2 py-1 outline-none focus:border-blue-500 text-xs font-medium cursor-pointer"
                >
                  <option value="original">Original</option>
                  <option value="1600">Max 1600px</option>
                  <option value="1280">Max 1280px</option>
                  <option value="1024">Max 1024px</option>
                  <option value="768">Max 768px</option>
                  <option value="512">Max 512px</option>
                  <option value="128">Max 128px</option>
                </select>
              )}
            </div>
            <div className="flex items-center bg-slate-50 p-2 rounded-lg border border-slate-200">
              <span className="text-sm font-medium text-slate-700 mr-2">Compression:</span>
              {isChangingCompressionMode ? (
                <span className="text-xs text-slate-400">Updating...</span>
              ) : (
                <select
                  value={compressionMode}
                  onChange={(e) => handleCompressionModeChange(e.target.value)}
                  className="bg-white border border-slate-300 rounded px-2 py-1 outline-none focus:border-blue-500 text-xs font-medium cursor-pointer"
                >
                  <option value="none">None</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button 
              onClick={runTestRig}
              disabled={isRunning === true || isRunning === null}
              className={`flex items-center flex-1 sm:flex-none justify-center px-4 py-2 rounded text-white font-medium transition-colors ${
                isRunning === true || isRunning === null ? 'bg-slate-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              <Play size={18} className="mr-1.5" />
              <span className="text-sm">{isRunning === true ? 'Running...' : isRunning === null ? 'Checking...' : 'Run Test'}</span>
            </button>
            <button 
              onClick={fetchData}
              className="flex items-center px-3 py-2 bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors"
              title="Refresh data"
            >
              <RefreshCcw size={18} className={loading ? 'animate-spin' : ''} />
              <span className="text-sm ml-1.5 hidden sm:inline">Refresh</span>
            </button>
            <button 
              onClick={flushResults}
              disabled={isRunning === true || isRunning === null || testResults.length === 0}
              className={`flex items-center px-3 py-2 rounded text-white transition-colors ${
                isRunning === true || isRunning === null || testResults.length === 0 ? 'bg-slate-400 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'
              }`}
              title="Clear all test results"
            >
              <Trash2 size={18} />
              <span className="text-sm ml-1.5 hidden sm:inline">Flush</span>
            </button>
          </div>
        </div>
      </div>


      <div className="mb-10 p-10 bg-slate-50 rounded-lg border border-slate-200 text-center">
        <div className="flex flex-col items-center justify-center max-w-lg mx-auto">
          <BarChart2 size={64} className="text-slate-300 mb-4" />
          <h2 className="text-xl font-bold text-slate-700 mb-2">Detailed Analytics Engine</h2>
          <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
            <div 
              className="bg-green-500 h-full transition-all duration-1000" 
              style={{ width: isRunning === true ? '60%' : '100%' }}
            ></div>
          </div>
          <p className="text-xs text-slate-400 mt-2">
            {isRunning === true ? 'Currently analyzing images...' : 'System ready for full dataset pass'}
          </p>
        </div>
      </div>

      <h3 className="text-xl font-semibold mb-4 text-slate-800">Recent Test Runs</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="p-4 font-semibold text-slate-700"></th>
              <th className="p-4 font-semibold text-slate-700">Timestamp</th>
              <th className="p-4 font-semibold text-slate-700">Model</th>
              <th className="p-4 font-semibold text-slate-700">Resize</th>
              <th className="p-4 font-semibold text-slate-700">Compression</th>
              <th className="p-4 font-semibold text-slate-700">Mode</th>
              <th className="p-4 font-semibold text-slate-700">Data Points</th>
              <th className="p-4 font-semibold text-slate-700">Avg Speed</th>
              <th className="p-4 font-semibold text-slate-700">Accuracy</th>
              <th className="p-4 font-semibold text-slate-700">Cost</th>
            </tr>
          </thead>
          <tbody>
            {testResults.map((run, idx) => (
              <React.Fragment key={idx}>
                <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setExpandedRun(expandedRun === idx ? null : idx)}>
                  <td className="p-4 text-sm text-slate-600">
                    {expandedRun === idx ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </td>
                  <td className="p-4 text-sm text-slate-600">{new Date(run.timestamp).toLocaleString()}</td>
                  <td className="p-4 text-sm text-slate-600">{run.model || 'N/A'}</td>
                  <td className="p-4 text-sm text-slate-600">{run.resize_mode || 'N/A'}</td>
                  <td className="p-4 text-sm text-slate-600">{run.compression_mode || 'N/A'}</td>
                  <td className="p-4 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          run.mode === 'MOCK' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                      }`}>
                          {run.mode || 'UNKNOWN'}
                      </span>
                  </td>
                  <td className="p-4 text-sm text-slate-600">{run.data_points || run.iterations || run.num_iterations || 0}</td>
                  <td className="p-4 text-sm text-slate-600">{(run.avg_speed_ms || run.avg_speed || 0).toFixed(0)} ms</td>
                  <td className="p-4 text-sm font-bold text-slate-700">{(run.avg_accuracy * 100 || 0).toFixed(1)}%</td>
                  <td className="p-4 text-sm text-slate-600">${(run.total_cost || 0).toFixed(4)}</td>
                </tr>
                {expandedRun === idx && (
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <td colSpan="10" className="p-4">
                      <div className="bg-white rounded border border-slate-200 shadow-inner overflow-hidden">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="bg-slate-100">
                              <th className="p-2 border-r border-b">Lot#</th>
                              <th className="p-2 border-r border-b">Image</th>
                              <th className="p-2 border-r border-b">Item Name</th>
                              <th className="p-2 border-r border-b">Actual Price</th>
                              <th className="p-2 border-r border-b">AI Estimate</th>
                              <th className="p-2 border-r border-b">AI Est. Sum</th>
                              <th className="p-2 border-r border-b">Accuracy</th>
                              <th className="p-2 border-r border-b">Duration</th>
                              <th className="p-2 border-b">Cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {run.results?.map((res, ridx) => {
                              const itemCount = res.items?.length || 1;
                              return (
                              <React.Fragment key={ridx}>
                                {res.items?.map((item, iidx) => (
                                  <tr key={`${ridx}-${iidx}`} className={`${iidx < itemCount - 1 ? '' : 'border-b'} hover:bg-slate-50`}>
                                    {iidx === 0 && (
                                      <td className="p-2 border-r align-top" rowSpan={itemCount}>{item['lot#'] || res['lot#'] || '-'}</td>
                                    )}
                                    {iidx === 0 && (
                                      <td className="p-2 border-r align-top" rowSpan={itemCount}>
                                        <div className="flex flex-wrap gap-1 max-w-[150px]">
                                          {(res.image_urls || item.image_urls || [item.image_url]).filter(url => url).map((url, uidx) => (
                                            <img 
                                              key={uidx}
                                              src={url} 
                                              alt={`${item.item} ${uidx}`} 
                                              onClick={(e) => {
                                                  e.stopPropagation();
                                                  setSelectedImage(url);
                                                  setSelectedImages(res.image_urls || item.image_urls || [item.image_url]);
                                                  setSelectedLotNum(item['lot#'] || res['lot#']);
                                                  setSelectedItems(res.items);
                                              }}
                                              className="w-10 h-10 object-cover rounded shadow-sm hover:scale-[1.5] hover:z-10 transition-transform cursor-pointer"
                                            />
                                          ))}
                                          {!(res.image_urls?.length || item.image_urls?.length || item.image_url) && (
                                            <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center text-[10px] text-slate-400">No Img</div>
                                          )}
                                        </div>
                                      </td>
                                    )}
                                    <td className="p-2 border-r font-medium">{item.item}</td>
                                    {iidx === 0 && (
                                      <td className="p-2 border-r align-top" rowSpan={itemCount}>${item.actual?.toFixed(2)}</td>
                                    )}
                                    <td className="p-2 border-r">${item.estimated?.toFixed(2)}</td>
                                    {iidx === 0 && (
                                      <td className="p-2 border-r align-top font-bold" rowSpan={itemCount}>${res.items.reduce((sum, i) => sum + (i.estimated || 0), 0).toFixed(2)}</td>
                                    )}
                                    {iidx === 0 && (() => {
                                      const aiSum = res.items.reduce((sum, i) => sum + (i.estimated || 0), 0);
                                      const actual = item.actual || 0;
                                      const acc = actual !== 0 && aiSum !== 0 ? Math.min(aiSum, actual) / Math.max(aiSum, actual) : (actual === 0 && aiSum === 0 ? 1 : 0);
                                      return (
                                        <td className="p-2 border-r align-top" rowSpan={itemCount}>
                                          <span className={`px-1.5 py-0.5 rounded font-bold ${
                                            acc > 0.8 ? 'bg-green-100 text-green-700' : 
                                            acc > 0.5 ? 'bg-yellow-100 text-yellow-700' : 
                                            'bg-red-100 text-red-700'
                                          }`}>
                                            {(acc * 100).toFixed(1)}%
                                          </span>
                                        </td>
                                      );
                                    })()}
                                    {iidx === 0 && (
                                      <td className="p-2 border-r align-top" rowSpan={itemCount}>{(res.duration * 1000).toFixed(0)} ms</td>
                                    )}
                                    {iidx === 0 && (
                                      <td className="p-2 align-top" rowSpan={itemCount}>${res.cost?.toFixed(4)}</td>
                                    )}
                                  </tr>
                                ))}
                              </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {testResults.length === 0 && (
              <tr>
                <td colSpan="10" className="p-8 text-center text-slate-400 italic">No test runs found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <ImageModal 
        isOpen={!!selectedImage} 
        onClose={() => {
            setSelectedImage(null);
            setSelectedImages([]);
            setSelectedLotNum(null);
            setSelectedItems(null);
        }}
        imageUrl={selectedImage}
        imageUrls={selectedImages}
        lotNum={selectedLotNum}
        items={selectedItems}
      />
    </div>
  );
};

export default TestRigPage;
